import { ref, computed, type ComputedRef } from "vue";
import { FLAGS, type FlagDefinition, type FlagKey, type FlagValue } from "./flags";

// FLAGS is empty until the next flag is added; cast so indexing type-checks in the meantime
// (Record<FlagKey, ...> would collapse to {} while FlagKey is never).
const flagDefinitions = FLAGS as Record<string, FlagDefinition>;

const STORAGE_KEY = "feature-flags";

type FlagState = Partial<Record<FlagKey, unknown>>;

function loadState(): FlagState {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");

    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const state = ref<FlagState>(loadState());

function getValue<K extends FlagKey>(key: K): FlagValue<K> {
  const stored = state.value[key];
  return (stored !== undefined ? stored : flagDefinitions[key].defaultValue) as FlagValue<K>;
}

export function setFlag<K extends FlagKey>(key: K, value: FlagValue<K>): void {
  state.value = { ...state.value, [key]: value };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state.value));
}

export function resetFlags(): void {
  state.value = {};
  localStorage.removeItem(STORAGE_KEY);
}

type FlagComputeds = { [K in FlagKey]: ComputedRef<FlagValue<K>> };

export function useFlags(): FlagComputeds {
  return Object.fromEntries(
    (Object.keys(FLAGS) as FlagKey[]).map((key) => [key, computed(() => getValue(key))]),
  ) as FlagComputeds;
}

export function flagAbEnabled<A, B>(key: FlagKey, valueIfTrue: A, valueIfFalse: B): A | B {
  return getValue(key) ? valueIfTrue : valueIfFalse;
}

export function isFlagEnabled(key: FlagKey): boolean {
  return getValue(key);
}
